/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { catchError, combineLatest, first, mergeMap, Observable, switchMap, throwError } from "rxjs";
import { INTERACTION_ID, KEYCLOAK_CONFLICT_ERROR_MESSAGES } from "src/app/constants";
import { KeycloakTokenRefreshService } from "src/app/services/keycloak-token-refresh.service";
import { FromUser, FromProgram, RootState, UserActions } from "src/app/store";
import { environment } from "src/environments/environment";

interface KeycloakError {
  errorMessage: string;
}

@Injectable({ providedIn: "root" })
export class AuthInterceptor implements HttpInterceptor {
  private readonly store = inject(Store<RootState>);
  private readonly keycloakTokenRefreshService = inject(KeycloakTokenRefreshService);

  private readonly accessToken$: Observable<string | undefined> = this.store.select(FromUser.selectAccessToken);
  private readonly keycloakUrl$ = this.store.select(FromProgram.selectKeycloakUrl) as Observable<string | undefined>;
  private readonly keycloakRealm$ = this.store.select(FromProgram.selectKeycloakRealm) as Observable<string | undefined>;
  private readonly keycloakClientId$ = this.store.select(FromProgram.selectKeycloakClientId) as Observable<string | undefined>;
  private readonly refreshToken$: Observable<string | undefined> = this.store.select(FromUser.selectRefreshToken);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return combineLatest([this.accessToken$, this.refreshToken$, this.keycloakClientId$, this.keycloakUrl$, this.keycloakRealm$]).pipe(
      first(),
      switchMap(([accessToken, refreshToken, keycloakClientId, keycloakUrl, keycloakRealm]) => {
        if (!this.isAssetsRequest(req) && !this.isKeycloakApiRequest(req, keycloakUrl) && !this.isToomRequest(req)) {
          req = req.clone({ setHeaders: { "interaction-id": INTERACTION_ID } });
        }

        if (accessToken && !this.isToomRequest(req)) {
          req = req.clone({ setHeaders: { authorization: `Bearer ${accessToken}` } });
        }

        return next.handle(req).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status >= HttpStatusCode.InternalServerError.valueOf()) console.error(error);
            if (this.isCorsError(error) || error.status === HttpStatusCode.NotFound.valueOf()) return throwError(() => error);

            if (
              error.status === HttpStatusCode.Unauthorized.valueOf() ||
              error.status === 0 ||
              this.isKeycloakAdminApiRequest(req, keycloakUrl) ||
              this.isKeycloakConflictError(error as unknown as KeycloakError)
            ) {
              if (this.isToomRequest(req)) return throwError(() => error);
              if (error.status === HttpStatusCode.Conflict.valueOf() || this.isKeycloakConflictError(error as unknown as KeycloakError)) {
                return throwError(() => error);
              }

              if (refreshToken && keycloakClientId && keycloakUrl && keycloakRealm) {
                // Re-read after the 401 in case another request already refreshed the token.
                return this.accessToken$.pipe(
                  first(),
                  switchMap(currentAccessToken => {
                    if (currentAccessToken && currentAccessToken !== accessToken) {
                      return next.handle(req.clone({ setHeaders: { authorization: `Bearer ${currentAccessToken}` } }));
                    }

                    return this.keycloakTokenRefreshService
                      .refreshAccessToken(refreshToken, keycloakClientId, keycloakUrl, keycloakRealm)
                      .pipe(
                        mergeMap(newAccessToken => next.handle(req.clone({ setHeaders: { authorization: `Bearer ${newAccessToken}` } }))),
                      );
                  }),
                );
              } else {
                this.store.dispatch(UserActions.keycloakLogout());
              }

              return throwError(() => error);
            }

            return throwError(() => error);
          }),
        );
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  private isKeycloakApiRequest(req: HttpRequest<any>, keycloakUrl: string | undefined): boolean {
    return !!keycloakUrl && req.url.includes(keycloakUrl);
  }

  private isKeycloakAdminApiRequest(req: HttpRequest<any>, keycloakUrl: string | undefined): boolean {
    return req.url.includes(`${keycloakUrl}/admin`);
  }

  private isToomRequest(req: HttpRequest<any>): boolean {
    return req.url.includes(environment.toomApi) || req.url.includes(environment.toomTokenExchange);
  }

  private isAssetsRequest(req: HttpRequest<any>): boolean {
    return req.url.includes("/assets/i18n");
  }

  private isCorsError(error: HttpErrorResponse): boolean {
    return error instanceof HttpErrorResponse && !error.ok && error.status === 0 && error.error instanceof ProgressEvent;
  }

  private isKeycloakConflictError(error: KeycloakError): boolean {
    return KEYCLOAK_CONFLICT_ERROR_MESSAGES.includes(error.errorMessage);
  }
}
